import React from 'react';
import { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X } from 'lucide-react';
import { PriorityLevel, WishlistItem } from '../types';
import { format } from 'date-fns';

interface WishlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  householdId: string;
  editingItem?: WishlistItem | null;
}

export default function WishlistModal({ isOpen, onClose, userId, householdId, editingItem }: WishlistModalProps) {
  const [name, setName] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [priorityLevel, setPriorityLevel] = useState<PriorityLevel>('Medium');
  const [targetDate, setTargetDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingItem) {
      setName(editingItem.name);
      setEstimatedCost(editingItem.estimatedCost.toString());
      setPriorityLevel(editingItem.priorityLevel);
      setTargetDate(editingItem.targetDate ? format(editingItem.targetDate, 'yyyy-MM-dd') : '');
    } else {
      setName('');
      setEstimatedCost('');
      setPriorityLevel('Medium');
      setTargetDate('');
    }
  }, [editingItem, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !estimatedCost || isNaN(Number(estimatedCost))) return;
    
    setLoading(true);
    try {
      const itemData = {
        name,
        estimatedCost: parseFloat(estimatedCost),
        priorityLevel: priorityLevel as PriorityLevel,
        targetDate: targetDate ? new Date(targetDate) : null,
        userId,
        householdId,
      };

      if (editingItem && editingItem.id) {
        await updateDoc(doc(db, 'wishlist', editingItem.id), itemData);
      } else {
        await addDoc(collection(db, 'wishlist'), {
          ...itemData,
          createdAt: serverTimestamp()
        });
      }
      onClose();
      if (!editingItem) {
        setName('');
        setEstimatedCost('');
        setPriorityLevel('Medium');
        setTargetDate('');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold mb-6 text-slate-900">{editingItem ? 'Edit Wishlist Item' : 'Add to Family Wishlist'}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Item Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="e.g. Family Trip, New Telescope"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Estimated Cost</label>
            <input
              type="number"
              step="0.01"
              value={estimatedCost}
              onChange={(e) => setEstimatedCost(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Priority Level</label>
            <select
              value={priorityLevel}
              onChange={(e) => setPriorityLevel(e.target.value as PriorityLevel)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="High">High Priority</option>
              <option value="Medium">Medium Priority</option>
              <option value="Low">Low Priority</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Note: Fixed Expenses and Debts will always have strict precedence over wishlist items.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Target Date (Optional)</label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              When do you hope to fulfill this wish?
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-blue-600 text-white rounded-md font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 mt-4 shadow-sm transition-colors"
          >
            {loading ? (editingItem ? 'Saving...' : 'Adding...') : (editingItem ? 'Save Changes' : 'Add to Wishlist')}
          </button>
        </form>
      </div>
    </div>
  );
}
